import React, { FC, useEffect, useRef, useState } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import excelIcon from '../../../../assets/image/excel.svg';
import useMediaQuery from '../../../../lib/custom-hooks/useMediaQuery';
import breakpoints from '../../../../constants/breakpoints.constants';
import { iconSize } from '../../../../constants/icon.constants';
import {
  DesktopRowHeaderCellWidth,
  DesktopSpecCellWidth,
  MobileRowHeaderCellWidth,
  MobileSpecCellWidth,
  TableMaxWidth,
  TGridCSSVariables,
} from '../../../../constants/table.constants';
import { documentService, windowService } from '../../../../utils/window.utils';
import useStickyWrapper from '../../../../hooks/useStickyWrapper';
import Icon from '../../../@shared/icon';
import { TTableCompProps } from '../../types/index.types';
import ColumnHeadingElement from './columnHeadingElement';
import FixedColHeading from './fixedColHeading';
import TableHeader from './header';
import RowItems from './row';

import '../../../../styles/@shared/specs-table.scss';

const SpecificationTable: FC<WithTranslation & TTableCompProps> = ({ model, t }) => {
  const GridRef = useRef(null);
  const TableWrapperRef = useRef(null);
  const [leftMostVisibleColumn, setLeftMostVisibleColumn] = useState(0);
  const [showHiddenRightCells, setShowHiddenRightCells] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [fixedColumnHeading, setFixedColumnHeading] = useState(false);
  const [expandedRows, setExpandedRows] = useState([0]);
  const [hideArrowButton, setHideArrowButton] = useState(false);
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints['tbl-p']}px)`);
  const stickyWrapperEle = useStickyWrapper();

  const [dropdownAtColumnIndex, setDropdownAtColumnIndex] = useState<number | null>(null);

  // css variables
  const [gridColumns, setGridColumns] = useState('');
  const [gridScrollBy, setGridScrollBy] = useState('');
  const [gridLeftCellsMoveBy, setGridLeftCellsMoveBy] = useState('');
  const [gridFixedColHeadsMoveBy, setGridFixedColHeadsMoveBy] = useState('');
  const [gridFirstColumnWidth, setGridFirstColumnWidth] = useState('');
  const [gridColumnWidth, setGridColumnWidth] = useState('');
  const [gridOutsideSpaceHalved, setGridOutsideSpaceHalved] = useState('');

  // excel to be implemented in later versions
  /*  const specificationUrlParameters = [
      // exportData?.id,
      // exportData?.language,
      // exportData?.lineId,
      // exportData?.serieId,
      // exportData?.modelId,
    ].join('/');*/

  useEffect(() => {
    if (!GridRef?.current || !gridColumns) return;
    const gridEl = GridRef.current as HTMLElement | null;
    if (!gridEl) return;
    gridEl.style.setProperty(TGridCSSVariables.gridColumns, gridColumns);
    gridEl.style.setProperty(TGridCSSVariables.gridScrollBy, gridScrollBy);
    gridEl.style.setProperty(TGridCSSVariables.gridLeftCellsMoveBy, gridLeftCellsMoveBy);
    gridEl.style.setProperty(TGridCSSVariables.gridFixedColHeadsMoveBy, gridFixedColHeadsMoveBy);
    gridEl.style.setProperty(TGridCSSVariables.gridFirstColumnWidth, gridFirstColumnWidth);
    gridEl.style.setProperty(TGridCSSVariables.gridColumnWidth, gridColumnWidth);
    gridEl.style.setProperty(TGridCSSVariables.gridOutsideSpaceHalved, gridOutsideSpaceHalved);
  }, [
    gridColumns,
    gridScrollBy,
    gridLeftCellsMoveBy,
    gridFixedColHeadsMoveBy,
    gridFirstColumnWidth,
    gridColumnWidth,
    gridOutsideSpaceHalved,
    GridRef,
  ]);

  useEffect(() => {
    if (GridRef && GridRef.current && model.colHeadings.length) {
      const gridEl = GridRef.current as HTMLElement | null;
      if (!gridEl) return;
      const colLength = String(model.hideRowsHeader ? model.colHeadings.length - 1 : model.colHeadings.length);
      setGridColumns(() => colLength);
    }
  }, [GridRef, model.colHeadings]); // eslint-disable-line

  const showOrHideFixedColumn = (
    gridElDim: DOMRect,
    stickyWrapperEle: HTMLElement | null,
    topMostEmptyColHeadingDim: DOMRect,
    previousTop: number,
    fixedColShowing: boolean,
    setFixedColumnHeading: React.Dispatch<React.SetStateAction<boolean>>
  ): boolean => {
    let visibleTopLimit = stickyWrapperEle?.getBoundingClientRect().height || 0;
    let visibleBottomLimit = visibleTopLimit;

    // handling scroll down and removing the sticky row height if it is visible
    if (visibleTopLimit > 0 && gridElDim.top > previousTop && fixedColShowing) {
      visibleTopLimit -= topMostEmptyColHeadingDim.height;
      visibleBottomLimit -= topMostEmptyColHeadingDim.height;
    }

    // handling scroll up and adding buffer space of sticky row height if it is hidden
    if (gridElDim.top < previousTop && !fixedColShowing) {
      visibleBottomLimit += topMostEmptyColHeadingDim.height;
    }

    // check if table is visible and table top is above visible point and bottom is within view
    if (gridElDim.top < visibleTopLimit && gridElDim.bottom > visibleBottomLimit) {
      setFixedColumnHeading(() => true);
      return true;
    } else {
      setFixedColumnHeading(() => false);
      return false;
    }
  };

  useEffect(() => {
    let fixedColShowing = false;
    let previousTop = 0;

    function scrollHandler(): void {
      if (GridRef && GridRef.current && model.stickyHeadingColumn) {
        const gridEl = GridRef.current as HTMLElement | null;
        if (!gridEl) return;
        const gridElDim = gridEl.getBoundingClientRect();

        const topMostEmptyColHeadingDim = gridEl
          .getElementsByClassName('model-detail-specification-table__col-heading-dropdown__wrapper')?.[0]
          ?.getBoundingClientRect();

        if (!topMostEmptyColHeadingDim) return;

        fixedColShowing = showOrHideFixedColumn(
          gridElDim,
          stickyWrapperEle,
          topMostEmptyColHeadingDim,
          previousTop,
          fixedColShowing,
          setFixedColumnHeading
        );
        previousTop = gridElDim.top;
      }
    }

    documentService().addEventListener('scroll', scrollHandler);
    return (): void => {
      documentService().removeEventListener('scroll', scrollHandler);
    };
  }, [GridRef, stickyWrapperEle, model.stickyHeadingColumn]);

  const handleHideRowsHeaderResize = (maxGridWidth: number, cellWidth: number, columnWidth: string) => {
    // eslint-disable-next-line
    if (maxGridWidth - model.colHeadings.length * cellWidth > 0) {
      // eslint-disable-next-line
      const newColumnWidth = maxGridWidth / model.colHeadings.length + 'px';
      setGridFirstColumnWidth(() => newColumnWidth);
      setHideArrowButton(true);
      return newColumnWidth;
    } else {
      const firstColWidth = cellWidth + 'px';
      setGridFirstColumnWidth(() => firstColWidth);
      setHideArrowButton(false);
      return columnWidth;
    }
  };

  const handleShowRowsHeaderResize = (maxGridWidth: number, cellWidth: number, headerCellWidth: number, columnWidth: string) => {
    // eslint-disable-next-line
    if (maxGridWidth - model.colHeadings.length * cellWidth > headerCellWidth) {
      // eslint-disable-next-line
      const newColumnWidth = (maxGridWidth - headerCellWidth) / model.colHeadings.length + 'px';
      setHideArrowButton(true);
      return newColumnWidth;
    } else {
      setHideArrowButton(false);
      return columnWidth;
    }
  };

  const handleShowOrHideRowsHeader = (maxGridWidth: number, cellWidth: number, columnWidth: string) => {
    const headerCellWidth = windowService().innerWidth >= breakpoints['tbl-p'] ? DesktopRowHeaderCellWidth : MobileRowHeaderCellWidth;
    let newColumnWidth: string;
    if (model.hideRowsHeader || model.isEmptyFirstColumn) {
      newColumnWidth = handleHideRowsHeaderResize(maxGridWidth, cellWidth, columnWidth);
    } else {
      newColumnWidth = handleShowRowsHeaderResize(maxGridWidth, cellWidth, headerCellWidth, columnWidth);
    }
    return newColumnWidth;
  };

  const handleGridOutsideSpace = (clientWidth: number, TableMaxWidth: number) => {
    if (clientWidth && clientWidth > TableMaxWidth) {
      const remainingWidth = clientWidth - TableMaxWidth;
      setGridOutsideSpaceHalved(() => `${remainingWidth / 2}px`);
    } else {
      setGridOutsideSpaceHalved('0px');
    }
  };

  useEffect(() => {
    function resizeHandler(): void {
      if (GridRef && GridRef.current) {
        const gridEl = GridRef.current as HTMLElement | null;
        if (!gridEl) return;
        setGridScrollBy(() => '0');
        setGridLeftCellsMoveBy(() => '0');
        setGridFixedColHeadsMoveBy(() => '0');
        setLeftMostVisibleColumn(0);
        setExpandedRows([model.groupItemsIndexes?.[0] || 0]);
        setSearchTerm('');

        const clientWidth = documentService().documentElement.clientWidth;
        const maxGridWidth = Math.min(clientWidth, TableMaxWidth);
        const cellWidth = windowService().innerWidth >= breakpoints['tbl-p'] ? DesktopSpecCellWidth : MobileSpecCellWidth;
        let columnWidth = cellWidth + 'px';

        columnWidth = handleShowOrHideRowsHeader(maxGridWidth, cellWidth, columnWidth);
        setGridColumnWidth(() => columnWidth);
        handleGridOutsideSpace(clientWidth, TableMaxWidth);
      }
    }

    resizeHandler();
    windowService().addEventListener('resize', resizeHandler);
    return (): void => {
      windowService().removeEventListener('resize', resizeHandler);
    };
  }, [GridRef, model.hideRowsHeader]); // eslint-disable-line

  useEffect(() => {
    if (isDesktop && leftMostVisibleColumn > -1 && GridRef && GridRef.current && model.colHeadings.length > 0) {
      let newGridScrollBy = DesktopSpecCellWidth * leftMostVisibleColumn;
      const clientWidth = Math.min(documentService().documentElement.clientWidth, TableMaxWidth);
      const maxMove =
        (model.hideRowsHeader ? 0 : DesktopRowHeaderCellWidth) + DesktopSpecCellWidth * model.colHeadings.length - clientWidth; // eslint-disable-line
      if (newGridScrollBy > maxMove && maxMove > 0) {
        newGridScrollBy = maxMove;
      }
      const gridEl = GridRef.current as HTMLElement | null;
      if (!gridEl) return;
      setGridScrollBy(() => `-${newGridScrollBy}px`);
      if (model.stickyHeadingRow) {
        setGridLeftCellsMoveBy(() => `${newGridScrollBy}px`);
      }
      setGridFixedColHeadsMoveBy(() => `-${newGridScrollBy}px`);
    }
  }, [leftMostVisibleColumn, GridRef, model.colHeadings, model.hideRowsHeader, model.stickyHeadingRow, isDesktop]);

  useEffect(() => {
    // Closing dropdown if clicked anywhere else
    function handleClick(event): void {
      if (!isDesktop) return;
      if (!event?.target?.getAttribute('data-is-dropdown-switch')) {
        setDropdownAtColumnIndex(null);
      }
    }

    const docElement = documentService();
    if (!docElement?.body) return;

    docElement.addEventListener('click', handleClick, false);
    return (): void => {
      docElement.removeEventListener('click', handleClick, false);
    };
  }, [isDesktop]);

  useEffect(() => {
    // prevent background body scrolling when model is opened in mobile view
    if (!isDesktop) {
      const docElement = documentService();
      if (!docElement?.body) return;

      if (dropdownAtColumnIndex !== null) docElement.body.classList.add('body--with-spec-model');
      else docElement.body.classList.remove('body--with-spec-model');
    }
  }, [dropdownAtColumnIndex, isDesktop]);

  useEffect(() => {
    if (!searchTerm || !model.items) return;
    setExpandedRows(model.groupItemsIndexes);
  }, [searchTerm]); // eslint-disable-line

  const handleScroll = (e): void => {
    e.stopPropagation();
    if (!showHiddenRightCells) setShowHiddenRightCells(true);
    if (TableWrapperRef && TableWrapperRef.current) {
      const tableWrapperEl = TableWrapperRef.current as HTMLElement | null;
      if (!tableWrapperEl) return;
      if (tableWrapperEl.scrollLeft > -1) {
        setGridLeftCellsMoveBy(() => `${tableWrapperEl.scrollLeft}px`);
        setGridFixedColHeadsMoveBy(() => `-${tableWrapperEl.scrollLeft}px`);
      }
    }
  };

  const moveGridLeft = (): void => {
    if (GridRef && GridRef.current) {
      setLeftMostVisibleColumn((cardNo) => (cardNo > 0 ? cardNo - 1 : 0));
    }
  };

  const moveGridRight = (): void => {
    if (TableWrapperRef && TableWrapperRef.current && model.nOfVisibleColumn) {
      const tableWrapperEl = TableWrapperRef.current as HTMLElement | null;
      if (!tableWrapperEl) return;
      const availableWidth = tableWrapperEl.clientWidth - (model.hideRowsHeader ? 0 : DesktopRowHeaderCellWidth);
      const visibleColumns = Math.floor(availableWidth / DesktopSpecCellWidth);
      const maxMove = model.nOfVisibleColumn - 1 - visibleColumns;

      if (!showHiddenRightCells) setShowHiddenRightCells(true);

      setLeftMostVisibleColumn((cardNo) => (cardNo <= maxMove ? cardNo + 1 : cardNo));
    }
  };

  const toggleAllRows = (): void => {
    if (!model.groupItemsIndexes.length) return;
    if (expandedRows.length === model.groupItemsIndexes.length) {
      setExpandedRows([]);
    } else {
      setExpandedRows(model.groupItemsIndexes);
    }
  };

  const toggleRow = (index: number): void => {
    if (GridRef && GridRef.current) {
      const arrIndex = expandedRows.findIndex((val) => val === index);
      if (arrIndex > -1) {
        setExpandedRows((prevExpandedRows) => prevExpandedRows.filter((val) => val !== index));
      } else {
        setExpandedRows((prevExpandedRows) => [...prevExpandedRows, index]);
      }
    }
  };

  const printTable = (): void => {
    // to be implemented in later version
  };

  return (
    <div className="model-detail-specification-table-wrapper">
      <TableHeader
        model={model}
        isDesktop={isDesktop}
        excelIcon={excelIcon}
        printTable={printTable}
        expandedRows={expandedRows}
        toggleAllRows={toggleAllRows}
        hideArrowButton={hideArrowButton}
        moveGridLeft={moveGridLeft}
        moveGridRight={moveGridRight}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
      />

      {searchTerm && !model.items?.length && (
        <div className="model-detail-specification-table__search-no-match">
          <Icon iconName="action-close-circle" size={iconSize.xlg} extraClassName="table-circle-alert-icon" />
          <p className="model-detail-specification-table__search-no-match__title">{t('cnhi-noMatchesFoundTitle')}</p>
          <p className="model-detail-specification-table__search-no-match__desc">{t('cnhi-noMatchesFoundDescription')}</p>
        </div>
      )}
      {(!searchTerm || (searchTerm && model.items?.length > 0)) && (
        <div className="model-detail-specification-table" ref={TableWrapperRef} onScroll={handleScroll}>
          <table className="model-detail-specification-table__table-tag" cellPadding="0" cellSpacing="0" ref={GridRef}>
            <FixedColHeading
              fixedColumnHeading={fixedColumnHeading}
              hideRowsHeader={model.hideRowsHeader}
              gridColumns={gridColumns}
              gridFixedColHeadsMoveBy={gridFixedColHeadsMoveBy}
              gridFirstColumnWidth={gridFirstColumnWidth}
              gridColumnWidth={gridColumnWidth}
              gridOutsideSpaceHalved={gridOutsideSpaceHalved}
              gridLeftCellsMoveBy={gridLeftCellsMoveBy}
            >
              <ColumnHeadingElement colHeadings={model.colHeadings} showHiddenRightCells={showHiddenRightCells} />
            </FixedColHeading>
            {!model.hideColumnHeader && (
              <thead>
                <tr>
                  {!model.hideRowsHeader && (
                    <th className="model-detail-specification-table__empty model-detail-specification-table__left-most-cell">
                      <div className="model-detail-specification-table__left-cell-width" />
                    </th>
                  )}
                  <ColumnHeadingElement colHeadings={model.colHeadings} showHiddenRightCells={showHiddenRightCells} />
                </tr>
              </thead>
            )}
            <tbody>
              {model.items?.map((obj, index) => (
                <RowItems
                  key={obj.title?.value}
                  obj={obj}
                  model={model}
                  toggleRow={toggleRow}
                  expandedRows={expandedRows}
                  index={index}
                  showHiddenRightCells={showHiddenRightCells}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default withTranslation()(SpecificationTable);
